<script setup>
// ============================================================================
// BonBoZ: trang Thống kê > CẢM XÚC — khách đang thấy thế nào, ai đang phụ
// trách những khách tiêu cực. Tham khảo bố cục Retion, không bê nguyên.
//
// Trên xuống: hai Ô LỚN (tích cực / tiêu cực — bấm vào là ra danh sách từng
// tin) → biểu đồ hai cột theo ngày → bảng "ai phụ trách". CHỈ tích cực và
// tiêu cực, không có trung tính — tin hỏi giá hỏi giờ (đại đa số) không nói
// gì về thái độ khách, đếm nó chỉ làm hai con số đáng nhìn bị chìm.
//
// Nhãn do engine TỪ ĐIỂN gắn ở backend (Bonboz::PhanLoaiCamXuc) — trang này
// chỉ đọc cột messages.sentiment, engine sau này đổi (LLM riêng) UI không đổi.
//
// Danh sách chi tiết là MODAL chứ không phải trang riêng (giống Retion):
// người xem đang đứng ở tổng quan, soi vài tin rồi quay lại — bắt đi sang
// trang khác rồi bấm back là cắt mạch. Khác Retion: mỗi dòng có link mở
// THẲNG hội thoại — xem xong tin giận dữ thì việc tiếp theo luôn là vào dỗ
// khách, không phải chỉ để đọc.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HieuSuatAPI from 'dashboard/api/bonboz/hieuSuat';
import { useAlert } from 'dashboard/composables';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import { useMapGetter } from 'dashboard/composables/store';
import { ngay } from 'dashboard/helper/dinhDangNgay';
import { dinhDangThoiLuong } from 'dashboard/helper/thoiLuong';
import {
  taiXuongExcel,
  tenFileThongKe,
} from 'dashboard/helper/bonboz/xuatExcel';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import BieuDoCamXuc from './BieuDoCamXuc.vue';
import ReportHeader from '../components/ReportHeader.vue';
import OverviewReportFilters from '../components/OverviewReportFilters.vue';
import PhamVi from './PhamVi.vue';

const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const { selectedInboxIds, isAllSelected } = useSelectedPages();
const inboxes = useMapGetter('inboxes/getInboxes');

const dangTai = ref(true);
const tong = ref(null);
const chuoiNgay = ref([]);
const nhanVien = ref([]);
const leoThang = ref([]);
// Gấp bớt khối leo thang sau dòng thứ 5 — khai ở đây vì tai() reset nó.
const moRongLeoThang = ref(false);
const tu = ref(null);
const den = ref(null);

// Tab phạm vi — cùng khuôn với các trang Thống kê khác.
const bangCua = ref(null); // null = tổng các trang

const cacTrangChon = computed(() => {
  if (isAllSelected.value) return inboxes.value;
  const chon = new Set(selectedInboxIds.value.map(Number));
  return inboxes.value.filter(hop => chon.has(Number(hop.id)));
});

const phamViInbox = () =>
  bangCua.value === null ? selectedInboxIds.value : [bangCua.value];

const tai = async () => {
  if (!tu.value || !den.value) return;
  dangTai.value = true;
  try {
    const { data } = await HieuSuatAPI.camXuc({
      since: tu.value,
      until: den.value,
      inboxIds: phamViInbox(),
    });
    tong.value = data.tong;
    chuoiNgay.value = data.ngay || [];
    nhanVien.value = data.nhan_vien || [];
    leoThang.value = data.leo_thang || [];
    moRongLeoThang.value = false;
  } catch {
    tong.value = null;
    chuoiNgay.value = [];
    nhanVien.value = [];
    leoThang.value = [];
    useAlert(t('BONBOZ.CAM_XUC.LOI_TAI'));
  } finally {
    dangTai.value = false;
  }
};

watch(selectedInboxIds, () => {
  bangCua.value = null;
  tai();
});

const chonBang = id => {
  bangCua.value = id;
  tai();
};

const doiBoLoc = ({ from, to }) => {
  tu.value = from;
  den.value = to;
  bangCua.value = null;
  tai();
};

const coSoLieu = computed(
  () => (tong.value?.tich_cuc ?? 0) + (tong.value?.tieu_cuc ?? 0) > 0
);

// Khối leo thang KHÔNG bao giờ giấu ai — nhiều hơn 5 khách thì gấp bớt lại
// sau dòng thứ 5, kèm nút "Xem thêm N khách". Chính lúc cháy to là lúc cần
// đọc nhanh nhất, một khối đỏ dài cả màn hình thì không ai đọc nhanh được.
const HIEN_TRUOC = 5;
const leoThangHien = computed(() =>
  moRongLeoThang.value ? leoThang.value : leoThang.value.slice(0, HIEN_TRUOC)
);
const soAn = computed(() => Math.max(0, leoThang.value.length - HIEN_TRUOC));

// Chỉ hiện những nhân viên CÓ tin mang cảm xúc — người toàn tin trung tính
// đứng trong bảng chỉ là một dòng hai số 0.
const bangNhanVien = computed(() =>
  nhanVien.value.filter(d => d.tich_cuc + d.tieu_cuc > 0)
);

const nhanBieuDo = computed(() =>
  chuoiNgay.value.map(d => ngay(d.moc * 1000, 'ngayThang'))
);

// ------------------------------------------------ MODAL DANH SÁCH CHI TIẾT
const hopChiTiet = ref(null);
const nhanDangXem = ref('negative');
const dongChiTiet = ref([]);
const tongSo = ref(0);
const trangHienTai = ref(1);
const dangTaiChiTiet = ref(false);
const MOI_TRANG = 20;

const taiChiTiet = async () => {
  dangTaiChiTiet.value = true;
  try {
    const { data } = await HieuSuatAPI.camXucChiTiet({
      label: nhanDangXem.value,
      since: tu.value,
      until: den.value,
      inboxIds: phamViInbox(),
      page: trangHienTai.value,
      perPage: MOI_TRANG,
    });
    dongChiTiet.value = data.tin || [];
    tongSo.value = data.tong_so || 0;
  } catch {
    dongChiTiet.value = [];
    tongSo.value = 0;
    useAlert(t('BONBOZ.CAM_XUC.LOI_TAI'));
  } finally {
    dangTaiChiTiet.value = false;
  }
};

const moChiTiet = nhan => {
  nhanDangXem.value = nhan;
  trangHienTai.value = 1;
  hopChiTiet.value.open();
  taiChiTiet();
};

const tongTrang = computed(() =>
  Math.max(1, Math.ceil(tongSo.value / MOI_TRANG))
);

const doiTrang = huong => {
  const moi = trangHienTai.value + huong;
  if (moi < 1 || moi > tongTrang.value) return;
  trangHienTai.value = moi;
  taiChiTiet();
};

// Xuất TOÀN BỘ danh sách của nhãn đang xem, không phải mỗi trang đang hiện —
// người bấm "Tải xuống Excel" muốn cả kỳ. Trần 2000 dòng để một cú bấm không
// kéo nửa bảng messages về trình duyệt; quá trần thì file vẫn ra kèm cảnh báo
// ở dòng cuối.
const xuatChiTiet = async () => {
  try {
    const { data } = await HieuSuatAPI.camXucChiTiet({
      label: nhanDangXem.value,
      since: tu.value,
      until: den.value,
      inboxIds: phamViInbox(),
      page: 1,
      perPage: 2000,
    });
    const cacHang = [
      [
        t('BONBOZ.CAM_XUC.COT_LUC'),
        t('BONBOZ.CAM_XUC.COT_TRANG'),
        t('BONBOZ.CAM_XUC.COT_KHACH'),
        t('BONBOZ.CAM_XUC.COT_NOI_DUNG'),
        t('BONBOZ.CAM_XUC.COT_TRACH_NHIEM'),
        t('BONBOZ.CAM_XUC.COT_CACH'),
        t('BONBOZ.CAM_XUC.COT_PHU_TRACH'),
      ],
      ...(data.tin || []).map(d => [
        ngay(d.luc * 1000, 'ngayGio'),
        d.trang,
        d.khach,
        d.noi_dung,
        d.trach_nhiem ? d.trach_nhiem.ten : t('BONBOZ.CAM_XUC.CHUA_AI_TRA_LOI'),
        // Kèm khoảng cách để bản tải về tự kiểm lại được, không phải mở lại trang.
        d.trach_nhiem
          ? dinhDangThoiLuong(d.trach_nhiem.cach) +
            (d.trach_nhiem.qua_lau ? ` ${t('BONBOZ.CAM_XUC.QUA_LAU')}` : '')
          : '',
        d.phu_trach,
      ]),
    ];
    if ((data.tong_so || 0) > 2000) {
      cacHang.push([t('BONBOZ.CAM_XUC.QUA_TRAN', { n: data.tong_so })]);
    }
    taiXuongExcel(
      tenFileThongKe(`cam-xuc-${nhanDangXem.value}`, tu.value, den.value),
      cacHang
    );
  } catch {
    useAlert(t('BONBOZ.CAM_XUC.LOI_TAI'));
  }
};
</script>

<template>
  <ReportHeader
    :header-title="$t('BONBOZ.CAM_XUC.TIEU_DE')"
    :header-description="$t('BONBOZ.CAM_XUC.MO_TA')"
  />

  <div class="flex flex-col gap-4">
    <OverviewReportFilters :disabled="dangTai" @filter-change="doiBoLoc" />

    <PhamVi />

    <!-- TAB PHẠM VI — ngoài nhánh đang-tải/trống để luôn quay về Tổng được. -->
    <div
      v-if="cacTrangChon.length > 1"
      class="flex flex-wrap items-center gap-1 p-1.5 border rounded-xl border-n-strong bg-n-slate-2 w-fit max-w-full"
      role="tablist"
    >
      <button
        v-for="tab in [
          { id: null, ten: $t('BONBOZ.TRANG_TK.BANG_TONG') },
          ...cacTrangChon.map(hop => ({ id: Number(hop.id), ten: hop.name })),
        ]"
        :key="tab.id ?? 'tong'"
        role="tab"
        :aria-selected="bangCua === tab.id"
        :disabled="dangTai"
        class="px-3.5 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap"
        :class="
          bangCua === tab.id
            ? 'bg-n-brand text-white font-medium shadow-sm'
            : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
        "
        @click="chonBang(tab.id)"
      >
        {{ tab.ten }}
      </button>
    </div>

    <div
      v-if="dangTai"
      class="flex items-center justify-center gap-2 py-16 text-sm text-n-slate-11"
    >
      <Spinner :size="24" class="text-n-brand" />
      {{ $t('BONBOZ.CAM_XUC.DANG_TAI') }}
    </div>

    <EmptyState v-else-if="!coSoLieu" :title="$t('BONBOZ.CAM_XUC.CHUA_CO')" />

    <template v-else>
      <!-- ====== LEO THANG — khách giận nhiều lần, xử TRƯỚC tiên ====== -->
      <section
        v-if="leoThang.length"
        class="p-4 border-2 rounded-xl border-n-ruby-8 bg-n-ruby-2"
      >
        <h3
          class="flex items-center gap-2 m-0 mb-1 text-sm font-semibold text-n-ruby-11"
        >
          <span class="i-lucide-siren size-4 shrink-0" aria-hidden="true" />
          {{ $t('BONBOZ.CAM_XUC.LEO_THANG') }}
        </h3>
        <p class="m-0 mb-3 text-xs text-n-ruby-11/80">
          {{ $t('BONBOZ.CAM_XUC.GT_LEO_THANG') }}
        </p>
        <ul class="flex flex-col gap-2 p-0 m-0 list-none">
          <li
            v-for="d in leoThangHien"
            :key="d.hoi_thoai"
            class="flex flex-wrap items-center gap-x-3 gap-y-1 p-2.5 rounded-lg bg-n-background"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ d.khach }}
            </span>
            <span
              class="px-1.5 py-0.5 text-xs rounded-md tabular-nums bg-n-ruby-3 text-n-ruby-11"
            >
              {{ $t('BONBOZ.CAM_XUC.SO_TIN_TIEU_CUC', { n: d.so }) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ d.phu_trach || $t('BONBOZ.CAM_XUC.CHUA_AI_NHAN') }}
            </span>
            <span
              class="flex-1 min-w-[10rem] text-xs italic truncate text-n-slate-11"
            >
              {{ d.tin_cuoi }}
            </span>
            <RouterLink
              :to="{
                name: 'inbox_conversation',
                params: { accountId, conversation_id: d.hoi_thoai },
                query: { messageId: d.tin_id },
              }"
              class="text-xs font-medium whitespace-nowrap rounded text-n-brand hover:underline"
            >
              {{ $t('BONBOZ.CAM_XUC.MO_HOI_THOAI') }}
            </RouterLink>
          </li>
        </ul>
        <button
          v-if="soAn > 0 || moRongLeoThang"
          type="button"
          class="mt-2 text-xs font-medium rounded text-n-ruby-11 hover:underline"
          @click="moRongLeoThang = !moRongLeoThang"
        >
          {{
            moRongLeoThang
              ? $t('BONBOZ.CAM_XUC.THU_GON')
              : $t('BONBOZ.CAM_XUC.XEM_THEM', { n: soAn })
          }}
        </button>
      </section>

      <!-- ============ HAI Ô LỚN — BẤM LÀ RA DANH SÁCH ============ -->
      <div class="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          class="flex items-center gap-4 p-5 border rounded-xl text-start transition-colors border-n-weak bg-n-solid-2 hover:border-n-teal-8"
          @click="moChiTiet('positive')"
        >
          <span
            class="flex items-center justify-center rounded-full size-11 shrink-0 bg-n-teal-3 text-n-teal-11"
          >
            <span class="i-lucide-smile size-6" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <span
              class="block text-2xl font-semibold tabular-nums text-n-teal-11"
            >
              {{ tong.tich_cuc }}
            </span>
            <span class="block text-sm text-n-slate-11">
              {{ $t('BONBOZ.CAM_XUC.O_TICH_CUC') }}
            </span>
          </span>
          <span
            class="ms-auto text-xs text-n-slate-10 whitespace-nowrap self-end"
          >
            {{ $t('BONBOZ.CAM_XUC.BAM_XEM') }}
          </span>
        </button>

        <button
          type="button"
          class="flex items-center gap-4 p-5 border rounded-xl text-start transition-colors border-n-weak bg-n-solid-2 hover:border-n-ruby-8"
          @click="moChiTiet('negative')"
        >
          <span
            class="flex items-center justify-center rounded-full size-11 shrink-0 bg-n-ruby-3 text-n-ruby-11"
          >
            <span class="i-lucide-frown size-6" aria-hidden="true" />
          </span>
          <span class="min-w-0">
            <span
              class="block text-2xl font-semibold tabular-nums text-n-ruby-11"
            >
              {{ tong.tieu_cuc }}
            </span>
            <span class="block text-sm text-n-slate-11">
              {{ $t('BONBOZ.CAM_XUC.O_TIEU_CUC') }}
            </span>
          </span>
          <span
            class="ms-auto text-xs text-n-slate-10 whitespace-nowrap self-end"
          >
            {{ $t('BONBOZ.CAM_XUC.BAM_XEM') }}
          </span>
        </button>
      </div>

      <!-- ============ BIỂU ĐỒ THEO NGÀY ============ -->
      <section class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <h3 class="m-0 mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('BONBOZ.CAM_XUC.BIEU_DO') }}
        </h3>
        <div class="h-64">
          <BieuDoCamXuc
            :nhan="nhanBieuDo"
            :tich-cuc="chuoiNgay.map(d => d.tich_cuc)"
            :tieu-cuc="chuoiNgay.map(d => d.tieu_cuc)"
            :ten-tich-cuc="$t('BONBOZ.CAM_XUC.O_TICH_CUC')"
            :ten-tieu-cuc="$t('BONBOZ.CAM_XUC.O_TIEU_CUC')"
          />
        </div>
      </section>

      <!-- ============ AI PHỤ TRÁCH ============ -->
      <section
        v-if="bangNhanVien.length"
        class="border rounded-xl border-n-strong bg-n-solid-2"
      >
        <div class="p-4 pb-3">
          <h3 class="m-0 text-sm font-medium text-n-slate-12">
            {{ $t('BONBOZ.CAM_XUC.BANG_NHAN_VIEN') }}
          </h3>
          <p class="m-0 mt-0.5 text-xs text-n-slate-11">
            {{ $t('BONBOZ.CAM_XUC.GT_BANG_NHAN_VIEN') }}
          </p>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead class="bg-n-slate-2">
              <tr class="text-xs border-y border-n-strong text-n-slate-11">
                <th class="px-4 py-2 font-normal text-start">
                  {{ $t('BONBOZ.CAM_XUC.COT_NHAN_VIEN') }}
                </th>
                <th class="px-4 py-2 font-normal text-end">
                  {{ $t('BONBOZ.CAM_XUC.O_TIEU_CUC') }}
                </th>
                <th class="px-4 py-2 font-normal text-end">
                  {{ $t('BONBOZ.CAM_XUC.O_TICH_CUC') }}
                </th>
                <th class="px-4 py-2 font-normal text-end">
                  {{ $t('BONBOZ.CAM_XUC.COT_TONG_TIN') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in bangNhanVien"
                :key="d.id ?? 'chua-ai'"
                class="border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
              >
                <td class="px-4 py-2.5">
                  <!-- Không có ai nhắn trước = khách bực vì BỊ BỎ RƠI. Đây
                       thường là dòng đáng chú ý nhất bảng nên tô đỏ. -->
                  <span
                    v-if="d.id === null"
                    class="px-2 py-0.5 text-xs rounded-md bg-n-ruby-3 text-n-ruby-11"
                  >
                    {{ $t('BONBOZ.CAM_XUC.KHONG_QUY_DUOC') }}
                  </span>
                  <span v-else class="flex items-center gap-1.5">
                    <span class="text-n-slate-12">{{ d.ten }}</span>
                    <!-- Bot trả lời sai là việc của đội chatbot, không phải
                         của trực page — phải phân biệt được ngay trên bảng. -->
                    <span
                      v-if="d.la_bot"
                      class="px-1.5 py-0.5 text-xxs rounded bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ $t('BONBOZ.CAM_XUC.LA_BOT') }}
                    </span>
                  </span>
                </td>
                <td
                  class="px-4 py-2.5 tabular-nums text-end"
                  :class="
                    d.tieu_cuc
                      ? 'font-medium text-n-ruby-11'
                      : 'text-n-slate-10'
                  "
                >
                  {{ d.tieu_cuc }}
                </td>
                <td
                  class="px-4 py-2.5 tabular-nums text-end"
                  :class="d.tich_cuc ? 'text-n-teal-11' : 'text-n-slate-10'"
                >
                  {{ d.tich_cuc }}
                </td>
                <td class="px-4 py-2.5 tabular-nums text-end text-n-slate-11">
                  {{ d.tong_tin }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <!-- ============ MODAL DANH SÁCH CHI TIẾT ============ -->
    <Dialog
      ref="hopChiTiet"
      width="3xl"
      :title="
        nhanDangXem === 'positive'
          ? $t('BONBOZ.CAM_XUC.CHI_TIET_TICH_CUC')
          : $t('BONBOZ.CAM_XUC.CHI_TIET_TIEU_CUC')
      "
      overflow-y-auto
    >
      <div class="flex flex-col gap-3 min-h-[16rem]">
        <div
          v-if="dangTaiChiTiet"
          class="flex items-center justify-center gap-2 py-16 text-sm text-n-slate-11"
        >
          <Spinner :size="20" class="text-n-brand" />
          {{ $t('BONBOZ.CAM_XUC.DANG_TAI') }}
        </div>

        <p
          v-else-if="!dongChiTiet.length"
          class="py-16 m-0 text-sm text-center text-n-slate-11"
        >
          {{ $t('BONBOZ.CAM_XUC.TRONG') }}
        </p>

        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="text-xs border-b border-n-strong text-n-slate-11">
                <th class="px-2 py-2 font-normal text-start whitespace-nowrap">
                  {{ $t('BONBOZ.CAM_XUC.COT_LUC') }}
                </th>
                <th class="px-2 py-2 font-normal text-start">
                  {{ $t('BONBOZ.CAM_XUC.COT_TRANG') }}
                </th>
                <th class="px-2 py-2 font-normal text-start">
                  {{ $t('BONBOZ.CAM_XUC.COT_KHACH') }}
                </th>
                <th class="px-2 py-2 font-normal text-start">
                  {{ $t('BONBOZ.CAM_XUC.COT_NOI_DUNG') }}
                </th>
                <th
                  v-tooltip.top="$t('BONBOZ.CAM_XUC.GT_COT_TRACH_NHIEM')"
                  class="px-2 py-2 font-normal text-start"
                >
                  {{ $t('BONBOZ.CAM_XUC.COT_TRACH_NHIEM') }}
                </th>
                <th
                  v-tooltip.top="$t('BONBOZ.CAM_XUC.GT_COT_PHU_TRACH')"
                  class="px-2 py-2 font-normal text-start"
                >
                  {{ $t('BONBOZ.CAM_XUC.COT_PHU_TRACH') }}
                </th>
                <th class="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="d in dongChiTiet"
                :key="d.id"
                class="align-top border-b border-n-weak last:border-b-0 hover:bg-n-alpha-1"
              >
                <td class="px-2 py-2 text-xs whitespace-nowrap text-n-slate-11">
                  {{ ngay(d.luc * 1000, 'ngayGio') }}
                </td>
                <td class="px-2 py-2 text-xs text-n-slate-11">{{ d.trang }}</td>
                <td class="px-2 py-2 whitespace-nowrap text-n-slate-12">
                  {{ d.khach }}
                </td>
                <td class="px-2 py-2 max-w-[22rem] text-n-slate-12">
                  {{ d.noi_dung }}
                </td>
                <td class="px-2 py-2 whitespace-nowrap text-n-slate-11">
                  <span v-if="d.trach_nhiem" class="flex items-center gap-1">
                    <span
                      :class="d.trach_nhiem.qua_lau ? 'text-n-slate-10' : ''"
                    >
                      {{ d.trach_nhiem.ten }}
                    </span>
                    <span
                      v-if="d.trach_nhiem.la_bot"
                      class="px-1 py-0.5 text-xxs rounded bg-n-alpha-2"
                    >
                      {{ $t('BONBOZ.CAM_XUC.LA_BOT') }}
                    </span>
                    <!-- Cách bao lâu: người đọc tự kiểm lại liên hệ nhân quả.
                         Quá ngưỡng thì làm mờ — bảng tổng cũng không tính. -->
                    <span class="text-xxs text-n-slate-10 whitespace-nowrap">
                      {{ dinhDangThoiLuong(d.trach_nhiem.cach) }}
                      {{
                        d.trach_nhiem.qua_lau
                          ? $t('BONBOZ.CAM_XUC.QUA_LAU')
                          : ''
                      }}
                    </span>
                  </span>
                  <span v-else class="text-n-ruby-11">
                    {{ $t('BONBOZ.CAM_XUC.CHUA_AI_TRA_LOI') }}
                  </span>
                </td>
                <td class="px-2 py-2 whitespace-nowrap text-n-slate-11">
                  {{ d.phu_trach || '—' }}
                </td>
                <td class="px-2 py-2">
                  <!-- Xem xong tin giận dữ thì việc tiếp theo là vào dỗ khách —
                       link mở thẳng hội thoại, không bắt đi tìm lại. -->
                  <!-- query.messageId: MessagesView cuộn + neo thẳng tới
                       đúng tin (cùng cơ chế với kết quả tìm kiếm) — tin có
                       thể nằm giữa một lịch sử dài, mở đáy hội thoại rồi bắt
                       dò bằng mắt là đánh đố. -->
                  <RouterLink
                    v-if="d.hoi_thoai"
                    :to="{
                      name: 'inbox_conversation',
                      params: { accountId, conversation_id: d.hoi_thoai },
                      query: { messageId: d.id },
                    }"
                    class="text-xs whitespace-nowrap rounded text-n-brand hover:underline"
                  >
                    {{ $t('BONBOZ.CAM_XUC.MO_HOI_THOAI') }}
                  </RouterLink>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <template #footer>
        <div class="flex flex-wrap items-center justify-between w-full gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-download"
            :label="$t('BONBOZ.XUAT.NUT')"
            :disabled="!tongSo"
            @click="xuatChiTiet"
          />
          <div class="flex items-center gap-2">
            <span class="text-xs tabular-nums text-n-slate-11">
              {{
                $t('BONBOZ.CAM_XUC.TRANG_N', {
                  n: trangHienTai,
                  m: tongTrang,
                  tong: tongSo,
                })
              }}
            </span>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-chevron-left"
              :disabled="dangTaiChiTiet || trangHienTai <= 1"
              :aria-label="$t('BONBOZ.CAM_XUC.TRANG_TRUOC')"
              @click="doiTrang(-1)"
            />
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              icon="i-lucide-chevron-right"
              :disabled="dangTaiChiTiet || trangHienTai >= tongTrang"
              :aria-label="$t('BONBOZ.CAM_XUC.TRANG_SAU')"
              @click="doiTrang(1)"
            />
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>
